import React from "react";
import {
  groupSquadsByDate,
  squadDateLabel,
  squadPickerLabel,
  squadTimeLabel,
} from "../ScoringHub/ScoringToolbar/scoringFilterOptions";

const TREE_LINE = "bg-brand-charcoal";

const Join = ({ from = 25, to = 75 }) => {
  const mid = (from + to) / 2;
  return (
    <div className="relative h-full w-full overflow-visible" aria-hidden="true">
      <div
        className="absolute box-border border-t-2 border-r-2 border-b-2 border-brand-charcoal"
        style={{
          top: `calc(${from}% - 1px)`,
          height: `calc(${to - from}% + 2px)`,
          left: -1,
          width: "calc(50% + 1px)",
        }}
      />
      <div
        className={`absolute left-1/2 right-0 h-[2px] ${TREE_LINE}`}
        style={{ top: `calc(${mid}% - 1px)` }}
      />
    </div>
  );
};

const Bridge = () => (
  <div className="relative h-full w-full overflow-visible" aria-hidden="true">
    <div
      className={`absolute -left-px right-0 h-[2px] ${TREE_LINE}`}
      style={{ top: "calc(25% - 1px)" }}
    />
    <div
      className={`absolute -left-px right-0 h-[2px] ${TREE_LINE}`}
      style={{ top: "calc(75% - 1px)" }}
    />
  </div>
);

const Lead = () => (
  <div className="relative h-full w-full overflow-visible" aria-hidden="true">
    <div
      className={`absolute left-0 right-0 h-[2px] ${TREE_LINE}`}
      style={{ top: "calc(50% - 1px)" }}
    />
  </div>
);

const TreeLine = () => (
  <div
    className={`pointer-events-none absolute left-0 right-0 top-1/2 h-[2px] -translate-y-1/2 ${TREE_LINE}`}
    aria-hidden="true"
  />
);

export const nameMatchesQuery = (name, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return false;
  return String(name || "").toLowerCase().includes(q);
};

const slotScore = (match, ticket) => {
  if (!ticket) return null;
  if (match.ticketScores?.[ticket.id] != null) return match.ticketScores[ticket.id];
  if (ticket.id === match.left?.id) return match.leftScore;
  if (ticket.id === match.right?.id) return match.rightScore;
  return null;
};

const PlayerSlot = ({ ticket, score, out, isBye, highlight, onSelect }) => {
  const name = isBye
    ? "Bye"
    : ticket?.bowlerName || ticket?.name || "Bowler";
  return (
    <div className="relative flex min-h-[2.5rem] flex-1">
      <TreeLine />
      <button
        type="button"
        disabled={!ticket}
        onClick={() => ticket && onSelect?.(ticket)}
        className={`absolute inset-x-0 bottom-1/2 flex w-full items-center justify-between gap-2 px-2 pb-0.5 text-left text-sm leading-tight ${
          highlight
            ? "text-brand-gold-ink font-bold"
            : out
              ? "text-brand-muted"
              : "text-brand-charcoal"
        } ${!ticket ? "cursor-default" : ""}`}
      >
        <span className={`truncate ${out ? "line-through" : ""}`}>{name}</span>
        {score != null && score !== "" ? (
          <span className="font-display font-bold tabular-nums shrink-0">
            {score}
          </span>
        ) : null}
      </button>
    </div>
  );
};

const matchSlots = (match) => {
  if (!match || match.status === "empty") {
    return [
      { ticket: null, isBye: false },
      { ticket: null, isBye: false },
    ];
  }
  const extra = match.extra || [];
  const players = [match.left, match.right, ...extra].filter(Boolean);
  const isBye = Boolean(match.left && !match.right && extra.length === 0);
  if (isBye) {
    return [
      { ticket: match.left, isBye: false },
      { ticket: null, isBye: true },
    ];
  }
  if (players.length) {
    return players.map((ticket) => ({ ticket, isBye: false }));
  }
  return [
    { ticket: null, isBye: false },
    { ticket: null, isBye: false },
  ];
};

const MatchStack = ({ match, highlightQuery, onSelectTicket }) => {
  if (!match || match.status === "empty") {
    return <div className="h-full" />;
  }

  const slots = matchSlots(match);
  const advancerIds = new Set((match.advancers || []).map((ticket) => ticket?.id));
  const decided =
    match.status &&
    match.status !== "pending" &&
    match.status !== "empty";
  const isBye = Boolean(match.left && !match.right && (match.extra || []).length === 0);

  const note =
    match.status === "chop"
      ? "Chop"
      : match.status === "both"
        ? "Both advance"
        : match.status === "pending"
          ? "Waiting on scores"
          : isBye
            ? "Bye"
            : null;

  const from = 50 / slots.length;
  const to = 100 - 50 / slots.length;

  return (
    <div className="relative flex h-full min-h-0 min-w-[10rem]">
      <div className="flex min-h-0 flex-1 flex-col">
        {slots.map((slot, index) => {
          const out =
            decided &&
            slot.ticket &&
            !advancerIds.has(slot.ticket.id) &&
            match.status !== "chop" &&
            match.status !== "both";
          const name = slot.isBye
            ? "Bye"
            : slot.ticket?.bowlerName || slot.ticket?.name || "";
          return (
            <PlayerSlot
              key={slot.ticket?.id || `empty-${index}`}
              ticket={slot.ticket}
              score={slotScore(match, slot.ticket)}
              out={out}
              isBye={slot.isBye}
              highlight={nameMatchesQuery(name, highlightQuery)}
              onSelect={onSelectTicket}
            />
          );
        })}
      </div>
      <div className="w-4 shrink-0">
        <Join from={from} to={to} />
      </div>
      {note ? (
        <div className="pointer-events-none absolute left-0 top-full px-1 text-[11px] text-brand-muted">
          {note}
        </div>
      ) : null}
    </div>
  );
};

const placeNameClass = (highlight, rest) =>
  `absolute inset-x-0 bottom-1/2 w-full px-2 pb-0.5 text-left text-sm ${
    highlight ? "text-brand-gold-ink font-bold" : rest
  }`;

const ResultCard = ({ resolved, highlightQuery, onSelectTicket }) => {
  if (resolved.chopped) {
    const names = (resolved.final.advancers || []).map(
      (ticket) => ticket?.bowlerName || ticket?.name || "Bowler"
    );
    return (
      <div className="flex h-full min-w-[8.5rem] flex-col justify-center">
        <div className="text-xs font-medium text-brand-muted mb-1">Chop</div>
        {names.map((name) => (
          <div key={name} className="relative min-h-[2.5rem]">
            <TreeLine />
            <div
              className={`absolute inset-x-0 bottom-1/2 px-2 pb-0.5 text-sm font-display font-bold ${
                nameMatchesQuery(name, highlightQuery) ? "text-brand-gold-ink" : ""
              }`}
            >
              {name}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const first = resolved.first;
  const second = resolved.second;
  const firstName = first?.bowlerName || first?.name || "—";
  const secondName = second?.bowlerName || second?.name || "—";
  return (
    <div className="relative h-full min-w-[8.5rem]">
      <div className="absolute inset-x-0 top-1/2 h-10 -translate-y-1/2">
        <div className="absolute inset-x-0 bottom-full px-2 text-xs font-medium text-brand-muted">
          1st
        </div>
        <TreeLine />
        <button
          type="button"
          disabled={!first}
          onClick={() => first && onSelectTicket?.(first)}
          className={placeNameClass(
            nameMatchesQuery(firstName, highlightQuery),
            "font-display font-bold"
          )}
        >
          {firstName}
        </button>
      </div>
      <div className="absolute inset-x-0 top-[calc(50%+2.75rem)] h-10">
        <div className="absolute inset-x-0 bottom-full px-2 text-xs font-medium text-brand-muted">
          2nd
        </div>
        <TreeLine />
        <button
          type="button"
          disabled={!second}
          onClick={() => second && onSelectTicket?.(second)}
          className={placeNameClass(
            nameMatchesQuery(secondName, highlightQuery),
            "text-brand-charcoal"
          )}
        >
          {secondName}
        </button>
      </div>
    </div>
  );
};

const TreeCard = ({ resolved, withNames, highlightQuery, onSelectTicket }) => {
  const named = (ticket) => {
    if (!ticket) return null;
    return { ...ticket, bowlerName: withNames.get(ticket.id) || ticket.bowlerName };
  };
  const nameMatch = (match) =>
    match
      ? {
          ...match,
          left: named(match.left),
          right: named(match.right),
          extra: (match.extra || []).map(named),
          advancers: (match.advancers || []).map(named),
          winner: named(match.winner),
        }
      : match;

  const round1 = [0, 1, 2, 3].map((slot) =>
    nameMatch(resolved.round1.find((match) => match.slot === slot) || resolved.round1[slot])
  );
  const round2 = [0, 1].map((slot) =>
    nameMatch(resolved.round2.find((match) => match.slot === slot) || resolved.round2[slot])
  );
  const final = nameMatch(resolved.final);
  const namedResolved = {
    ...resolved,
    first: named(resolved.first),
    second: named(resolved.second),
    final,
  };

  return (
    <article className="card-board p-4 space-y-3 break-inside-avoid max-w-full min-w-0">
      <h3 className="font-display font-bold text-brand-charcoal">
        Tree {resolved.tree.treeNumber}
        {resolved.tree.hasBye ? (
          <span className="ml-2 text-xs font-medium text-brand-muted">Bye</span>
        ) : null}
      </h3>
      <p className="text-sm text-brand-muted lg:hidden">
        Swipe sideways to see later games.
      </p>
      <div className="max-w-full overflow-x-auto pb-1">
      <div
        className="grid min-w-[44rem] gap-x-0"
        style={{
          gridTemplateColumns:
            "minmax(11.5rem,1fr) 1.25rem minmax(11.5rem,1fr) 1.25rem minmax(11.5rem,1fr) 1.25rem minmax(8.5rem,auto)",
          gridTemplateRows: "auto repeat(8, minmax(2.5rem, 1fr))",
        }}
      >
        <div className="text-xs font-medium text-brand-muted pb-2">Game 1</div>
        <div />
        <div className="text-xs font-medium text-brand-muted pb-2">Game 2</div>
        <div />
        <div className="text-xs font-medium text-brand-muted pb-2">Game 3</div>
        <div />
        <div className="text-xs font-medium text-brand-muted pb-2">Place</div>

        {round1.map((match, index) => (
          <div
            key={`r1-${index}`}
            style={{ gridColumn: 1, gridRow: `${index * 2 + 2} / ${index * 2 + 4}` }}
          >
            <MatchStack
              match={match}
              highlightQuery={highlightQuery}
              onSelectTicket={onSelectTicket}
            />
          </div>
        ))}
        <div style={{ gridColumn: 2, gridRow: "2 / 6" }}>
          <Bridge />
        </div>
        <div style={{ gridColumn: 2, gridRow: "6 / 10" }}>
          <Bridge />
        </div>
        <div style={{ gridColumn: 3, gridRow: "2 / 6" }}>
          <MatchStack
            match={round2[0]}
            highlightQuery={highlightQuery}
            onSelectTicket={onSelectTicket}
          />
        </div>
        <div style={{ gridColumn: 3, gridRow: "6 / 10" }}>
          <MatchStack
            match={round2[1]}
            highlightQuery={highlightQuery}
            onSelectTicket={onSelectTicket}
          />
        </div>
        <div style={{ gridColumn: 4, gridRow: "2 / 10" }}>
          <Bridge />
        </div>
        <div style={{ gridColumn: 5, gridRow: "2 / 10" }}>
          <MatchStack
            match={final}
            highlightQuery={highlightQuery}
            onSelectTicket={onSelectTicket}
          />
        </div>
        <div style={{ gridColumn: 6, gridRow: "2 / 10" }}>
          <Lead />
        </div>
        <div style={{ gridColumn: 7, gridRow: "2 / 10" }}>
          <ResultCard
            resolved={namedResolved}
            highlightQuery={highlightQuery}
            onSelectTicket={onSelectTicket}
          />
        </div>
      </div>
      </div>
    </article>
  );
};

const TreeBoard = ({
  resolvedTrees,
  tickets,
  bowlers,
  squads = [],
  highlightQuery = "",
  onSelectTicket,
  emptyMessage,
}) => {
  const names = new Map();
  (bowlers || []).forEach((bowler) => names.set(bowler.id, bowler.name));
  (tickets || []).forEach((ticket) => {
    const bowler = (bowlers || []).find((row) => row.id === ticket.bowlerID);
    names.set(ticket.id, bowler?.name || "Bowler");
  });

  const query = String(highlightQuery || "").trim();
  let trees = resolvedTrees || [];
  if (query) {
    trees = trees.filter((resolved) =>
      (tickets || []).some((ticket) => {
        if (ticket.treeID !== resolved.tree.id) return false;
        return nameMatchesQuery(names.get(ticket.id), query);
      })
    );
  }

  if (!trees.length) {
    return (
      <p className="text-sm text-brand-charcoal">
        {query
          ? "No trees have that name."
          : emptyMessage || "Shuffle tickets to build trees."}
      </p>
    );
  }

  const squadIdsInView = new Set(
    trees.map((resolved) => resolved.tree?.squadID).filter(Boolean)
  );
  const groupBySquad = squadIdsInView.size > 1;
  const groups = groupBySquad
    ? groupSquadsByDate(
        (squads || []).filter((squad) => squadIdsInView.has(squad.id))
      )
    : [{ date: "", squads: [{}] }];

  const cardsFor = (squadId) =>
    trees.filter((resolved) =>
      squadId ? resolved.tree?.squadID === squadId : true
    );

  return (
    <div className="space-y-8 print:block">
      {groupBySquad
        ? groups.map((group) => (
            <div key={group.date || "squads"} className="space-y-6">
              {groups.length > 1 ? (
                <h2 className="text-xs font-display font-bold uppercase tracking-wider text-brand-muted">
                  {squadDateLabel(group.date)}
                </h2>
              ) : null}
              {group.squads.map((squad) => {
                const cards = cardsFor(squad.id);
                if (!cards.length) return null;
                return (
                  <div key={squad.id} className="space-y-4">
                    <h3 className="font-display font-bold text-brand-charcoal">
                      {groups.length > 1
                        ? squadTimeLabel(squad.squadTime)
                        : squadPickerLabel(squad)}
                    </h3>
                    {cards.map((resolved) => (
                      <TreeCard
                        key={resolved.tree.id}
                        resolved={resolved}
                        withNames={names}
                        highlightQuery={query}
                        onSelectTicket={onSelectTicket}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          ))
        : trees.map((resolved) => (
            <TreeCard
              key={resolved.tree.id}
              resolved={resolved}
              withNames={names}
              highlightQuery={query}
              onSelectTicket={onSelectTicket}
            />
          ))}
    </div>
  );
};

export default TreeBoard;
